import { ElementType } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { AddFilledIcon, ChevronDownIcon, CloseFilledIcon } from "../../icons"
import { Typography } from "../../typography"

const Container = styled.div`
  position: relative;
`

const ChipButton = styled<ElementType>("button")`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px ${({ $hasValue }) => ($hasValue ? "solid" : "dashed")} ${({ theme }) => theme.colors.grey.$300};
  gap: 0.25rem;
  border-radius: 8.125rem;
  padding: 0.25rem;

  display: inline-flex;
  align-items: center;

  height: 1.375rem;

  color: ${({ theme }) => theme.colors.grey.$400};

  transition: all 0.3 ease;
  cursor: pointer;

  :hover {
    background-color: ${({ theme }) => theme.colors.grey.$100};
  }

  :focus {
    background-color: ${({ theme }) => theme.colors.grey.$100};
    outline: 2px solid ${({ theme }) => theme.colors.grey.$400};
  }

  :active {
    border-color: ${({ theme }) => theme.colors.black.$600};
    background-color: ${({ theme }) => theme.colors.white};
    outline: none;
  }
`

const LabelContainer = styled(Typography)`
  height: 1.125rem;
  line-height: 1.125rem;
  padding-right: 0.25rem;
  padding-left: 1rem;
  display: inline-flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.grey.$500};
`

const Sep = styled.hr`
  height: 1rem;
  margin: 0;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.colors.grey.$300};
`

const ValueContainer = styled(Typography)`
  padding-left: 0.25rem;
  height: 1.125rem;
  line-height: 1.125rem;

  color: ${({ theme }) => theme.colors.secondary.$200};
`

const IconButton = styled.button`
  border: none;
  color: ${({ theme }) => theme.colors.grey.$400};

  transition: all 0.3s ease;

  width: 1rem;
  height: 1rem;
  background: none;
  padding: 0;
  margin: 0;
  top: 0.3rem;
  left: 0.1875rem;
  cursor: pointer;

  position: absolute;

  :hover {
    color: ${({ theme }) => theme.colors.grey.$500};
  }
`
const Icon = styled(CloseFilledIcon)`
  width: 1rem;
  height: 1rem;
`
export type DeprecatedChipProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    label: React.ReactNode
    value?: React.ReactNode
    onClear: () => void
    as?: React.ElementType
  },
  C,
  C
>

export const DeprecatedChip = <C extends ElementType = "button">({
  label,
  value,
  onClear,
  onClick = () => {
    return
  },
  ...props
}: DeprecatedChipProps<C>) => {
  const hasValue = !!value
  return (
    <Container>
      <IconButton
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (hasValue) {
            onClear()
          } else {
            onClick(e)
          }
        }}
      >
        {hasValue ? <Icon /> : <Icon as={AddFilledIcon} />}
      </IconButton>
      <ChipButton type="button" $hasValue={hasValue} onClick={onClick} {...props}>
        <LabelContainer as="span" $variant="caption-1">
          {label as string}
        </LabelContainer>
        {hasValue ? (
          <>
            <Sep />
            <ValueContainer as="span" $variant="caption-1">
              {value}
            </ValueContainer>
            <Icon as={ChevronDownIcon} />
          </>
        ) : null}
      </ChipButton>
    </Container>
  )
}
